import { Tag } from "@aurem/ui";
import { Bot } from "lucide-react";
import { gql, InternalLlmAuditLogOrderByColumn } from "types";
import { FieldType, InternalResourceConfig } from "../lib/internal-resources";

const renderSuccess = (value: unknown) => {
  const success = Boolean(value);
  return (
    <Tag
      color={success ? "success" : "danger"}
      label={success ? "Success" : "Failed"}
    />
  );
};

const GET_LLM_AUDIT_LOG_LIST = gql(/* GraphQL */ `
  query InternalLLMAuditLogList(
    $pagination: Pagination
    $filters: FiltersExpression
    $orderBy: InternalLLMAuditLogOrderByInput
  ) {
    internalLLMAuditLogList(
      pagination: $pagination
      filters: $filters
      orderBy: $orderBy
    ) {
      count
      list {
        id
        runId
        nodeId
        createdAt
        modelName
        toolsDefinition
        maxTurns
        conversationHistory
        finalOutput
        success
        errorMessage
        turnCount
        totalDurationMs
        userId
        user {
          id
          profile {
            name
          }
        }
      }
    }
  }
`);

export const llmAuditLogResourceConfig: InternalResourceConfig = {
  resourceName: "llm-audit-log",
  displayNameKey: "apps.admin.internalResources.llmAuditLog.title",
  basePath: "/internal/data/llm-audit-log",

  listQuery: GET_LLM_AUDIT_LOG_LIST,
  listQueryName: "internalLLMAuditLogList",

  fields: [
    {
      key: "modelName",
      labelKey: "apps.admin.internalResources.llmAuditLog.columns.modelName",
      type: FieldType.String,
      sortKey: InternalLlmAuditLogOrderByColumn.ModelName,
      width: 180,
    },
    {
      key: "success",
      labelKey: "apps.admin.internalResources.llmAuditLog.columns.success",
      type: FieldType.String,
      format: renderSuccess,
      sortKey: InternalLlmAuditLogOrderByColumn.Success,
      width: 120,
    },
    {
      key: "runId",
      labelKey: "apps.admin.internalResources.llmAuditLog.columns.runId",
      type: FieldType.Uuid,
      width: 150,
    },
    {
      key: "nodeId",
      labelKey: "apps.admin.internalResources.llmAuditLog.columns.nodeId",
      type: FieldType.String,
      width: 120,
    },
    {
      key: "turnCount",
      labelKey: "apps.admin.internalResources.llmAuditLog.columns.turnCount",
      type: FieldType.String,
      sortKey: InternalLlmAuditLogOrderByColumn.TurnCount,
      width: 80,
    },
    {
      key: "totalDurationMs",
      labelKey:
        "apps.admin.internalResources.llmAuditLog.columns.totalDurationMs",
      type: FieldType.Duration,
      sortKey: InternalLlmAuditLogOrderByColumn.TotalDurationMs,
      width: 100,
    },
    {
      key: "conversationHistory",
      labelKey:
        "apps.admin.internalResources.llmAuditLog.columns.conversationHistory",
      type: FieldType.JSON,
      width: 120,
    },
    {
      key: "finalOutput",
      labelKey: "apps.admin.internalResources.llmAuditLog.columns.finalOutput",
      type: FieldType.JSON,
      width: 120,
    },
    {
      key: "errorMessage",
      labelKey: "apps.admin.internalResources.llmAuditLog.columns.errorMessage",
      type: FieldType.String,
      width: { min: 100, grow: 1 },
    },
    {
      key: "userId",
      labelKey: "apps.admin.internalResources.llmAuditLog.columns.userId",
      type: FieldType.User,
      width: 150,
    },
    {
      key: "createdAt",
      labelKey: "apps.admin.internalResources.llmAuditLog.columns.createdAt",
      type: FieldType.DateTime,
      sortKey: InternalLlmAuditLogOrderByColumn.CreatedAt,
      width: 160,
    },
  ],

  list: {
    defaultSort: {
      column: InternalLlmAuditLogOrderByColumn.CreatedAt,
      direction: "desc",
    },
    searchFields: ["llm_audit.model_name", "llm_audit.node_id"],
  },

  navigation: {
    icon: <Bot size={16} />,
    order: 2,
  },
};
